import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenuShortcut } from '@/components/ui/dropdown-menu'
import { endPoint } from '@/constant/end-point'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import type { IUserOrganisations } from '@/interface/auth.interface'
import type { IActiveOrg } from '@/interface/base.interface'
import { roleLabel } from '@/utils/role-label.utils'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export function OrgSelect({ organisations }: { organisations: IUserOrganisations['organisations'] | null }) {
  const { post } = useAPI()
  const { activeOrg, setActiveOrg } = useActiveOrg()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!activeOrg?.role) {
      if (organisations && organisations.length > 0) {
        const timer = setTimeout(() => {
          setOpen(true)
        }, 1000)
        return () => clearTimeout(timer)
      }
    } else setOpen(false)
  }, [activeOrg, organisations])

  const handleSetActiveOrg = async (org: IActiveOrg) => {
    setActiveOrg(org)
    try {
      const response = await post(endPoint.defaultOrganisation, {
        organisationId: org.id,
      })
      if (!response?.status) throw new Error(response?.message)
      setOpen(false)
    } catch (error) {
      toast.error((error as Error).message || 'Failed to switch organisation')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Select Organisation</DialogTitle>
          <DialogDescription>Select the organisation you want to switch to.</DialogDescription>
        </DialogHeader>

        <div>
          {organisations && organisations.length > 0 ? (
            organisations?.map((organisation, index) => {
              return (
                <div
                  className='flex items-center gap-2 cursor-pointer border p-4 rounded-sm'
                  key={index}
                  onClick={() =>
                    handleSetActiveOrg({
                      id: organisation.organisation.id,
                      name: organisation.organisation.name,
                      logo: organisation.organisation.logo,
                      role: organisation.role,
                      featurePermissions: organisation.featurePermissions,
                    })
                  }
                >
                  <div className='flex size-6 items-center justify-center rounded-md border'>
                    <Avatar className='h-8 w-8 rounded-lg'>
                      <AvatarImage
                        src={organisation.organisation.logo || ''}
                        alt={organisation.organisation.name || 'CY'}
                      />
                      <AvatarFallback className='rounded-lg uppercase'>
                        {organisation?.organisation?.name?.length && organisation?.organisation?.name?.length > 1 ? (
                          <>
                            {organisation?.organisation?.name?.charAt(0)}
                            {organisation?.organisation?.name?.charAt(1)}
                          </>
                        ) : (
                          <>CY</>
                        )}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className='flex flex-col'>
                    <p className='text-sm font-medium'>{organisation?.organisation?.name}</p>
                    <p className='text-xs text-muted-foreground -mt-[2px]'>{roleLabel(organisation?.role)}</p>
                  </div>
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </div>
              )
            })
          ) : (
            <div className='flex items-center gap-2 cursor-pointer border p-4 rounded-sm'>
              <div className='flex size-6 items-center justify-center rounded-md border'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={''} alt={'CY'} />
                  <AvatarFallback className='rounded-lg uppercase'>CY</AvatarFallback>
                </Avatar>
              </div>
              <div className='flex flex-col'>
                <p className='text-sm font-medium'>xxxx xxxx</p>
                <p className='text-xs text-muted-foreground -mt-[2px]'>xxxx xxxx</p>
              </div>
              <DropdownMenuShortcut>⌘1</DropdownMenuShortcut>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
